import { useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';

/** A JWT has exactly three dot-separated base64 segments. */
function looksLikeJwt(value: string): boolean {
  return value.split('.').length === 3;
}

/**
 * AuthCallbackPage handles the redirect from OAuth providers.
 *
 * Reads `?token=...&provider=...&zoom_connected=...` from the URL,
 * scrubs the token from the URL immediately, stores it in authStore,
 * calls checkSession to fetch user profile, then navigates to /chat.
 *
 * If no token or an error is present, redirects to /.
 */
export function AuthCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setAuth, setZoomConnected, checkSession } = useAuthStore();
  const hasRun = useRef(false);

  useEffect(() => {
    // Guard against StrictMode double-execution
    if (hasRun.current) return;
    hasRun.current = true;

    const token = searchParams.get('token');
    const error = searchParams.get('error');
    const zoomConnected = searchParams.get('zoom_connected');

    // Scrub token from URL immediately
    window.history.replaceState({}, '', '/auth/callback');

    if (error || !token || !looksLikeJwt(token)) {
      navigate('/', { replace: true });
      return;
    }

    // Set the token with a placeholder user
    setAuth({
      token,
      user: { id: '', email: '', name: null, avatarUrl: null },
      hasZoomConnection: zoomConnected === 'true',
    });

    if (zoomConnected === 'true') {
      setZoomConnected(true);
    }

    // Navigate immediately — checkSession runs in the background
    // (don't block navigation on /auth/me which may fail and trigger logout)
    navigate('/chat', { replace: true });

    // Fetch real user profile in background (non-blocking)
    checkSession();
  }, [searchParams, navigate, setAuth, setZoomConnected, checkSession]);

  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-4" />
        <p className="text-gray-600">Signing you in...</p>
      </div>
    </div>
  );
}
